import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { CheckCircle } from 'lucide-react';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { CreatorGroup } from '@/hooks/useGroupedCreators';
import { Creator } from '@/hooks/useCreators';

interface CreatorGroupCompactViewProps {
  groups: CreatorGroup[];
  onGroupClick: (group: CreatorGroup) => void;
}

export const CreatorGroupCompactView: React.FC<CreatorGroupCompactViewProps> = ({ groups, onGroupClick }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
    {groups.map((group) => (
      <div
        key={group.id}
        className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={() => onGroupClick(group)}
      >
        <Avatar className="h-16 w-16">
          <SafeAvatarImage src={getProxiedImageUrl(group.avatar_url)} className="object-cover" />
          <AvatarFallback className="bg-gradient-to-br from-muted to-muted/60">{(group.display_name || group.primary_name || 'U')[0]}</AvatarFallback>
        </Avatar>
        <div className="text-center min-w-0 w-full">
          <p className="text-sm font-medium truncate flex items-center justify-center gap-1">
            {group.display_name || group.primary_name}
            {group.is_verified && <CheckCircle className="h-3 w-3 text-primary shrink-0" />}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {group.platforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' • ')}
          </p>
        </div>
      </div>
    ))}
  </div>
);

interface CreatorCompactViewProps {
  creators: Creator[];
  onCreatorClick: (creator: Creator) => void;
}

export const CreatorCompactView: React.FC<CreatorCompactViewProps> = ({ creators, onCreatorClick }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
    {creators.map((creator) => (
      <div
        key={creator.id}
        className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={() => onCreatorClick(creator)}
      >
        <Avatar className="h-16 w-16">
          <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
          <AvatarFallback className="bg-gradient-to-br from-muted to-muted/60">{(creator.display_name || creator.username)[0]}</AvatarFallback>
        </Avatar>
        <div className="text-center min-w-0 w-full">
          <p className="text-sm font-medium truncate flex items-center justify-center gap-1">
            {creator.display_name}
            {creator.is_verified && <CheckCircle className="h-3 w-3 text-primary shrink-0" />}
          </p>
          <p className="text-xs text-muted-foreground truncate capitalize">{creator.platform}</p>
        </div>
      </div>
    ))}
  </div>
);
